// US-075 — Review-mode draft editor.
//
// Mirrors `ls-inbox.jsx:758-805`: a dashed-border draft bubble that flips
// to an inline `<Textarea>` on click. Toolbar surfaces "✎ Réécrire /
// ✓ Terminer", "Régénérer" (re-runs the SSE stream), and "Envoyer ↑"
// (commits to the API).
//
// State ownership: the parent (ThreadPanel) owns `body` so that a
// streaming AI draft can update it via `useDraftStream`. We expose the
// edit/regenerate/send callbacks; the parent decides how to wire them.
//
// Wave B3 — also renders an inline server-policy banner when the draft
// stream surfaces a typed `serverError` (US-180 quota CTA, US-086
// off-target block, US-089 channel-share lock). The banner short-circuits
// the editor textarea so the user doesn't waste time editing a draft
// that won't generate.

import { useState } from 'react';

import { useT } from '@barel/i18n';
import { Banner, Button, Textarea } from '@barel/ui';

import type { DraftStreamError } from './useDraftStream';

export interface ReviewEditorProps {
  body: string;
  onChange: (next: string) => void;
  onSend: () => void;
  onRegenerate: () => void;
  /** True while a regenerate / SSE stream is in flight. */
  streaming?: boolean;
  /** Disables the send button (e.g. while the request is in flight). */
  sending?: boolean;
  /**
   * Wave B3 — typed server error from `useDraftStream`. When set,
   * the editor renders a banner CTA instead of a draft toolbar.
   */
  serverError?: DraftStreamError | null;
  /** Wave B3 — opens the billing/upgrade page. Wired from the parent. */
  onUpgrade?: () => void;
}

export function ReviewEditor({
  body,
  onChange,
  onSend,
  onRegenerate,
  streaming = false,
  sending = false,
  serverError = null,
  onUpgrade,
}: ReviewEditorProps) {
  const t = useT('inbox');
  const [editing, setEditing] = useState(false);

  // Wave B3 — surface server-policy gates inline. The banner replaces the
  // editor surface so the user doesn't waste time on a draft that won't
  // generate. We only branch on the codes the server actually emits;
  // anything else falls through to the normal editor (the parent handles
  // generic errors via toast).
  if (serverError) {
    if (serverError.code === 'plan.ai_quota') {
      return (
        <li
          data-ls-component="review-editor"
          data-state="quota-exceeded"
          style={{
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1_5)',
          }}
        >
          <Banner tone="warning">
            <div className="font-semibold">{t('ai.quota_exceeded.title')}</div>
            <p className="text-xs">{t('ai.quota_exceeded.body')}</p>
            <div style={{ marginTop: 'var(--space-1_5)' }}>
              <Button variant="primary" size="sm" onClick={onUpgrade} data-ls-action="upgrade-plan">
                {t('ai.quota_exceeded.cta')}
              </Button>
            </div>
          </Banner>
        </li>
      );
    }
    if (serverError.code === 'conv.channel_share_locked') {
      return (
        <li
          data-ls-component="review-editor"
          data-state="channel-share-locked"
          style={{ listStyle: 'none' }}
        >
          <Banner tone="info">
            <div className="font-semibold">{t('ai.channel_share_locked.title')}</div>
            <p className="text-xs">{t('ai.channel_share_locked.body')}</p>
          </Banner>
        </li>
      );
    }
    if (serverError.code === 'conv.off_target_blocked') {
      return (
        <li
          data-ls-component="review-editor"
          data-state="off-target-blocked"
          style={{ listStyle: 'none' }}
        >
          <Banner tone="info">
            <div className="font-semibold">{t('ai.off_target_blocked.title')}</div>
            <p className="text-xs">{t('ai.off_target_blocked.body')}</p>
          </Banner>
        </li>
      );
    }
  }

  return (
    <li
      data-ls-component="review-editor"
      data-editing={editing || undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 'var(--space-1_5)',
        listStyle: 'none',
      }}
    >
      <span
        style={{
          fontSize: 'var(--text-2xs)',
          fontWeight: 700,
          color: 'var(--accent)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {t('thread.draft.kicker')}
      </span>

      {editing ? (
        <Textarea
          autoFocus
          value={body}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
            }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              setEditing(false);
              onSend();
            }
          }}
          autoResize
          minRows={3}
          size="md"
          surface="elevated"
          aria-label={t('thread.draft.editHint')}
          data-ls-review-editor-textarea
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="ls-focus-ring"
          title={t('thread.draft.editHint')}
          aria-label={t('thread.draft.editHint')}
          data-ls-component="message-bubble"
          data-role="draft"
          style={{
            background: 'var(--accent-dim)',
            color: 'var(--text-primary)',
            border: '1px dashed var(--accent-border)',
            borderRadius: 'var(--radius-bubble-out)',
            padding: 'var(--space-2_5) var(--space-3)',
            fontSize: 'var(--text-sm)',
            lineHeight: 'var(--line-sm)',
            fontFamily: 'var(--font-sans)',
            cursor: 'text',
            textAlign: 'left',
            maxWidth: '78%',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {body || ' '}
        </button>
      )}

      <div
        data-ls-region="review-toolbar"
        style={{
          display: 'flex',
          gap: 'var(--space-1_5)',
          alignItems: 'center',
        }}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditing((v) => !v)}
          data-ls-action="toggle-edit"
        >
          {editing ? t('thread.draft.doneEditing') : t('thread.draft.edit')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRegenerate}
          disabled={streaming}
          data-ls-action="regenerate"
        >
          {streaming ? `↻ …` : `↻ ${t('thread.draft.regenerate')}`}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onSend}
          disabled={sending || streaming || body.trim().length === 0}
          data-ls-action="send"
        >
          {t('thread.draft.send')}
        </Button>
      </div>
    </li>
  );
}
